import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Modal } from '../Modal';
import { log } from '../../utils/logger';
import "./QRCode.css";

type TariffBundlePrices = { price1: number; price2?: number; price3?: number; price4?: number };
type TariffsFromConfig = { tariff1: TariffBundlePrices; tariff2: TariffBundlePrices };

function tariffDisplayName(tariffType: string): string {
    return tariffType === '2' ? 'УСН с НДС' : 'УСН без НДС';
}

function periodLabel(tariffType: string, count: number): string {
    if (tariffType === '2') {
        if (count === 1) return 'квартал';
        if (count >= 2 && count <= 4) return 'квартала';
        return 'кварталов';
    }
    if (count === 1) return 'год';
    return 'лет';
}

function clampPeriods(tariffType: string, count: number): number {
    const max = tariffType === '2' ? 4 : 1;
    if (count < 1) return 1;
    if (count > max) return max;
    return count;
}

function priceForQuarters(t: TariffBundlePrices, quarters: number): number {
    if (quarters <= 1) return t.price1;
    if (quarters === 2) return t.price2 ?? t.price1 * 2;
    if (quarters === 3) return t.price3 ?? t.price1 * 3;
    if (quarters === 4) return t.price4 ?? t.price1 * 4;
    return (t.price4 ?? t.price1 * 4) + (quarters - 4) * t.price1;
}

/** Как на бэкенде: цена по сетке за (выбранные кварталы + доп. кварталы с даты открытия ИП). */
function subscriptionAmountRub(
    tariffs: TariffsFromConfig | null,
    tariffType: string,
    periodsCount: number,
    monthsBefore: number
): number | null {
    if (!tariffs) return null;
    const m = clampPeriods(tariffType, periodsCount);
    const key: keyof TariffsFromConfig = tariffType === '2' ? 'tariff2' : 'tariff1';
    const t = tariffs[key];
    if (!t) return null;
    if (tariffType === '1') {
        return t.price1;
    }
    return priceForQuarters(t, m);
}

function formatRub(n: number): string {
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

export const QRCode = () => {
    const { isAuthenticated, login, logout } = useAuth();
    const navigate = useNavigate();
    const [endpointAddress, setEndpointAddress] = useState('');
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const [monthsCount, setMonthsCount] = useState<number>(1);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentRequisites, setPaymentRequisites] = useState<string | null>(null);
    const [additionalServices, setAdditionalServices] = useState<
        Array<{ id: string; label: string; type: 'fixed' | 'individual'; price?: number; unit?: string }>
    >([]);
    const [selectedServices, setSelectedServices] = useState<Record<string, boolean>>({});
    const [additionalComment, setAdditionalComment] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const checkStarted = useRef(false);

    const [tariffsFromConfig, setTariffsFromConfig] = useState<TariffsFromConfig | null>(null);
    const [accountTariffType, setAccountTariffType] = useState<string>('1');
    const [monthsBefore, setMonthsBefore] = useState(0);

    const isQuarterlyTariff = accountTariffType === '2';
    const periodsCountSafe = clampPeriods(accountTariffType, monthsCount);

    useEffect(() => {
        setMonthsCount(1);
    }, [accountTariffType]);

    useEffect(() => {
        fetch('/config.json')
            .then(res => res.json())
            .then(config => {
                if (config['endpoint-address']) {
                    setEndpointAddress(config['endpoint-address']);
                }
                if (config['payment-requisites']) {
                    setPaymentRequisites(String(config['payment-requisites']));
                }
                const tr = config['tariffs'];
                if (tr && tr.tariff1 && tr.tariff2) {
                    const parseTariff = (x: unknown, quarterly: boolean): TariffBundlePrices | null => {
                        if (!x || typeof x !== 'object') return null;
                        const o = x as Record<string, unknown>;
                        if (typeof o.price1 !== 'number') return null;
                        if (!quarterly) {
                            return { price1: o.price1 };
                        }
                        if (
                            typeof o.price2 === 'number' &&
                            typeof o.price3 === 'number' &&
                            typeof o.price4 === 'number'
                        ) {
                            return {
                                price1: o.price1,
                                price2: o.price2,
                                price3: o.price3,
                                price4: o.price4,
                            };
                        }
                        return null;
                    };
                    const t1 = parseTariff(tr.tariff1, false);
                    const t2 = parseTariff(tr.tariff2, true);
                    if (t1 && t2) setTariffsFromConfig({ tariff1: t1, tariff2: t2 });
                }
                if (Array.isArray(config['additional-services'])) {
                    const list = config['additional-services']
                        .filter((s: any) => s && typeof s.id === 'string' && typeof s.label === 'string' && (s.type === 'fixed' || s.type === 'individual'))
                        .map((s: any) => ({
                            id: s.id,
                            label: s.label,
                            type: s.type as 'fixed' | 'individual',
                            price: typeof s.price === 'number' ? s.price : undefined,
                            unit: typeof s.unit === 'string' ? s.unit : undefined,
                        }));
                    setAdditionalServices(list);
                }
                log('QRCode', 'Загружен config', { hasEndpoint: !!config['endpoint-address'], hasRequisites: !!config['payment-requisites'] });
            })
            .catch((err) => log('QRCode', 'Ошибка загрузки config', err));
    }, []);

    const selectedAdditional = additionalServices.filter((s) => selectedServices[s.id]);
    const hasIndividualSelected = selectedAdditional.some((s) => s.type === 'individual');

    const additionalFixedTotal = selectedAdditional.reduce(
        (sum, s) => sum + (s.type === 'fixed' ? (s.price || 0) : 0),
        0
    );
    const additionalTotal = additionalFixedTotal;

    const subscriptionTotal = useMemo(
        () =>
            subscriptionAmountRub(
                tariffsFromConfig,
                accountTariffType,
                periodsCountSafe,
                monthsBefore
            ),
        [tariffsFromConfig, accountTariffType, periodsCountSafe, monthsBefore]
    );

    const currentYear = new Date().getFullYear();

    useEffect(() => {
        if (isAuthenticated) {
            const id = localStorage.getItem('id');
            const token = localStorage.getItem('token');
            if (!id || !token) {
                logout();
                navigate("/", { replace: true });
            }
        }
    }, [isAuthenticated, logout, navigate]);

    useEffect(() => {
        if (isAuthenticated || !endpointAddress) return;

        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) {
            navigate("/", { replace: true });
            return;
        }

        if (checkStarted.current) return;
        checkStarted.current = true;

        let cancelled = false;
        const url = `${endpointAddress}/checkSession?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;

        fetch(url, { method: 'GET' })
            .then(res => res.json().catch(() => ({})))
            .then((data: { status?: unknown }) => {
                if (cancelled) return;
                const statusOk = data?.status === true || data?.status === 'true';
                if (statusOk) {
                    login();
                } else {
                    navigate("/", { replace: true });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    checkStarted.current = false;
                    navigate("/", { replace: true });
                }
            });

        return () => { cancelled = true; };
    }, [isAuthenticated, login, endpointAddress, navigate]);

    useEffect(() => {
        if (!isAuthenticated || !endpointAddress) return;
        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) return;

        const url = `${endpointAddress}/getAccountInfo?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;
        fetch(url, { method: 'GET' })
            .then((res) => res.json().catch(() => null))
            .then((data: Record<string, unknown> | null) => {
                if (!data || typeof data !== 'object' || 'code' in data) return;
                const tt = data['tariff-type'];
                if (tt === '1' || tt === '2' || tt === 1 || tt === 2) {
                    setAccountTariffType(String(tt));
                }
                const mb = data['months-before'];
                if (typeof mb === 'number' && Number.isFinite(mb)) {
                    setMonthsBefore(Math.max(0, Math.floor(mb)));
                }
            })
            .catch(() => {});
    }, [isAuthenticated, endpointAddress]);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setUploadedImage(reader.result as string);
                };
                reader.readAsDataURL(file);
            } else {
                alert('Пожалуйста, выберите файл изображения');
            }
        }
    };

    const handleRemoveImage = () => {
        setUploadedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handlePay = async () => {
        if (hasIndividualSelected && !additionalComment.trim()) {
            alert('Для доп. услуги с индивидуальной стоимостью укажите комментарий');
            return;
        }

        if (!uploadedImage) {
            alert('Пожалуйста, загрузите чек');
            return;
        }

        const periodsToSend = isQuarterlyTariff ? periodsCountSafe : 1;
        if (isQuarterlyTariff && (periodsToSend < 1 || periodsToSend > 4)) {
            alert('Пожалуйста, выберите срок оплаты (от 1 до 4 кварталов)');
            return;
        }

        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');

        if (!id || !token) {
            alert('Ошибка авторизации. Пожалуйста, войдите заново.');
            navigate("/", { replace: true });
            return;
        }

        if (!endpointAddress) {
            alert('Ошибка подключения к серверу');
            return;
        }

        const base64Image = uploadedImage.includes(',')
            ? uploadedImage.split(',')[1]
            : uploadedImage;

        const requestBody = {
            id: id,
            token: token,
            checkPay: base64Image,
            months: periodsToSend.toString(),
            'additional-services': selectedAdditional,
            'additional-comment': additionalComment.trim(),
        };

        log('QRCode', 'Отправка чека', {
            endpoint: `${endpointAddress}/sendPayCheck`,
            periods: periodsToSend,
            checkPayLength: base64Image.length
        });

        setIsSubmitting(true);

        try {
            const response = await fetch(`${endpointAddress}/sendPayCheck`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json().catch(() => ({}));
            log('QRCode', 'Ответ сервера', { status: response.status, ok: response.ok, data });

            if (response.ok) {
                log('QRCode', 'Чек успешно отправлен');
                setSuccessModalOpen(true);
            } else {
                log('QRCode', 'Ошибка при отправке чека', { status: response.status, data });
                alert('Ошибка при отправке чека. Попробуйте еще раз.');
            }
        } catch (error) {
            log('QRCode', 'Исключение при отправке чека', error);
            alert('Ошибка при отправке чека. Попробуйте еще раз.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSuccessModalClose = () => {
        setSuccessModalOpen(false);
        navigate("/");
    };

    return (
        <div className="qr-code-page">
            <div className="qr-code-container">
                <h1 className="qr-code-title">Оплата услуг</h1>
                
                <div className="qr-code-section">
                    <h2 className="qr-code-subtitle">QR-код для оплаты</h2>
                    <div className="qr-code-image-wrapper">
                        <img 
                            src="/assets/qr-code.png" 
                            alt="QR-код для оплаты" 
                            className="qr-code-image"
                        />
                    </div>
                    <p className="qr-code-description">
                        Отсканируйте QR-код для перехода к оплате услуг
                    </p>
                    <p className="qr-code-hint">
                        В переводе укажите сумму из блока «Итого к переводу» ниже (с учётом срока оплаты, тарифа и доп. услуг).
                    </p>
                    {paymentRequisites && (
                        <div className="qr-code-requisites" dangerouslySetInnerHTML={{ __html: paymentRequisites }} />
                    )}
                </div>

                <div className="additional-services-section">
                    <h2 className="qr-code-subtitle">Дополнительные услуги</h2>
                    <p className="qr-code-description">
                        Отметьте нужные услуги. Указана стоимость за одну единицу (декларацию, уведомление и т.д.).
                        Администратор увидит выбранные услуги вместе с вашим чеком.
                    </p>
                    <div className="additional-services-list">
                        {additionalServices.map((s) => (
                            <label key={s.id} className="additional-service-item">
                                <input
                                    type="checkbox"
                                    checked={!!selectedServices[s.id]}
                                    onChange={(e) => setSelectedServices((prev) => ({ ...prev, [s.id]: e.target.checked }))}
                                />
                                <span className="additional-service-label">{s.label}</span>
                                <span className="additional-service-price">
                                    {s.type === 'fixed'
                                        ? `${formatRub(s.price ?? 0)} ₽${s.unit ? ` ${s.unit}` : ''}`
                                        : s.unit || 'индивидуально'}
                                </span>
                            </label>
                        ))}
                    </div>
                    <div className="additional-services-total">
                        <div className="additional-services-total-row">
                            <span>Итого доп. услуги (фикс. цена)</span>
                            <strong>{formatRub(additionalTotal)} ₽</strong>
                        </div>
                        <textarea
                            className="additional-services-textarea"
                            placeholder="Комментарий к заявке (обязательно для услуги с индивидуальной стоимостью)..."
                            value={additionalComment}
                            onChange={(e) => setAdditionalComment(e.target.value)}
                            rows={4}
                        />
                    </div>
                </div>

                <div className="months-section">
                    <h2 className="qr-code-subtitle">Срок оплаты</h2>
                    {isQuarterlyTariff ? (
                        <>
                            <p className="qr-code-description">
                                УСН с НДС: 3000 ₽ за квартал. При подключении — за текущий квартал; далее оплата 1 января,
                                1 апреля, 1 июля, 1 октября. Можно оплатить сразу несколько кварталов.
                            </p>
                            <div className="months-select-wrapper">
                                <select
                                    className="months-select"
                                    value={periodsCountSafe}
                                    onChange={(e) => setMonthsCount(Number(e.target.value))}
                                >
                                    <option value={1}>1 {periodLabel('2', 1)}</option>
                                    <option value={2}>2 {periodLabel('2', 2)}</option>
                                    <option value={3}>3 {periodLabel('2', 3)}</option>
                                    <option value={4}>4 {periodLabel('2', 4)}</option>
                                </select>
                            </div>
                        </>
                    ) : (
                        <p className="qr-code-description">
                            УСН без НДС: 2000 ₽ за календарный год ({currentYear} г., до 31.12.{currentYear}).
                            Продление — каждое 1 января. Выбор срока не требуется.
                        </p>
                    )}
                </div>

                <div className="payment-total-section">
                    <h2 className="qr-code-subtitle">Итого к переводу</h2>
                    {subscriptionTotal === null ? (
                        <p className="payment-total-unavailable">
                            Не удалось загрузить тарифные цены из конфигурации сайта. Укажите в переводе сумму по вашему
                            тарифу из раздела «Тариф» на главной или свяжитесь с поддержкой.
                        </p>
                    ) : (
                        <>
                            <div className="payment-total-breakdown">
                                <div className="payment-total-row">
                                    <span>
                                        Подписка (
                                        {isQuarterlyTariff
                                            ? `${periodsCountSafe} ${periodLabel('2', periodsCountSafe)}`
                                            : `календарный год ${currentYear}`}
                                        , {tariffDisplayName(accountTariffType)})
                                    </span>
                                    <strong>{formatRub(subscriptionTotal)} ₽</strong>
                                </div>
                                {monthsBefore > 0 && !isQuarterlyTariff && (
                                    <p className="payment-total-note">
                                        С даты открытия ИП прошло {monthsBefore} мес. — при необходимости уточните сумму у
                                        администратора.
                                    </p>
                                )}
                                {additionalTotal > 0 && (
                                    <div className="payment-total-row">
                                        <span>
                                            Дополнительные услуги (фикс.)
                                        </span>
                                        <strong>{formatRub(additionalTotal)} ₽</strong>
                                    </div>
                                )}
                                {hasIndividualSelected && (
                                    <p className="payment-total-note payment-total-note-warn">
                                        Услуги с индивидуальной стоимостью в сумму не входят — согласуйте оплату с
                                        администратором.
                                    </p>
                                )}
                            </div>
                            <div className="payment-total-grand">
                                <span>К переводу</span>
                                <strong>{formatRub(subscriptionTotal + additionalTotal)} ₽</strong>
                            </div>
                        </>
                    )}
                </div>

                <div className="upload-section">
                    <h2 className="qr-code-subtitle">Отправка чека</h2>
                    <div className="upload-area">
                        {uploadedImage ? (
                            <div className="uploaded-image-container">
                                <img 
                                    src={uploadedImage} 
                                    alt="Загруженный чек" 
                                    className="uploaded-image"
                                />
                                <button 
                                    className="remove-image-btn"
                                    onClick={handleRemoveImage}
                                >
                                    Удалить чек
                                </button>
                            </div>
                        ) : (
                            <label className="upload-label">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="upload-input"
                                />
                                <div className="upload-placeholder">
                                    <svg 
                                        className="upload-icon" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            strokeWidth={2} 
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                                        />
                                    </svg>
                                    <span className="upload-text">
                                        Нажмите для загрузки чека
                                    </span>
                                    <span className="upload-hint">
                                        или перетащите файл сюда
                                    </span>
                                </div>
                            </label>
                        )}
                    </div>
                </div>

                <div className="pay-button-section">
                    <button
                        className="pay-button"
                        onClick={handlePay}
                        disabled={isSubmitting || !uploadedImage}
                    >
                        {isSubmitting ? 'Отправка...' : 'Отправить чек'}
                    </button>
                </div>
            </div>

            <Modal
                isOpen={successModalOpen}
                onClose={handleSuccessModalClose}
                title="Чек отправлен"
                type="success"
                buttonText="На главную"
            >
                <p>Чек отправлен, в течении дня оплату подтвердят</p>
            </Modal>
        </div>
    );
};
